import React, { useEffect, useState } from 'react';
import { mysteryBoxAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Save, RotateCcw, Trophy, Award, Medal } from 'lucide-react';

const TIER_INFO = {
  1: { name: 'Baú do Destino',  color: 'from-amber-700 to-amber-900',   icon: Medal,  label: 'Bronze', emoji: '🥉' },
  2: { name: 'Baú Místico',     color: 'from-slate-400 to-slate-600',   icon: Award,  label: 'Prata',  emoji: '🥈' },
  3: { name: 'Baú do Segredo',  color: 'from-yellow-400 to-yellow-600', icon: Trophy, label: 'Ouro',   emoji: '🥇' },
};

const RARITY_BADGE = {
  lendario: { label: 'Lendário', cls: 'bg-purple-100 text-purple-800 border-purple-300' },
  epico:    { label: 'Épico',    cls: 'bg-pink-100 text-pink-800 border-pink-300' },
  raro:     { label: 'Raro',     cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  incomum:  { label: 'Incomum',  cls: 'bg-green-100 text-green-800 border-green-300' },
  comum:    { label: 'Comum',    cls: 'bg-gray-100 text-gray-700 border-gray-300' },
  basico:   { label: 'Básico',   cls: 'bg-stone-100 text-stone-700 border-stone-300' },
};

const MysteryBoxPrizesConfig = () => {
  const [prizes, setPrizes] = useState([]);
  const [edits, setEdits] = useState({}); // { prize_id: novaQuantidade }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await mysteryBoxAPI.getPrizes();
      setPrizes(res.data || []);
      setEdits({});
    } catch (e) {
      toast.error('Erro ao carregar prêmios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleChange = (prizeId, value) => {
    setEdits(prev => ({ ...prev, [prizeId]: value }));
  };

  const handleSave = async () => {
    const changedIds = Object.keys(edits);
    if (changedIds.length === 0) {
      toast.info('Nenhuma alteração para salvar');
      return;
    }
    setSaving(true);
    try {
      let saved = 0;
      for (const id of changedIds) {
        const newQty = parseInt(edits[id], 10);
        if (Number.isNaN(newQty) || newQty < 0) continue;
        await mysteryBoxAPI.updatePrizeQuantity(id, newQty);
        saved++;
      }
      toast.success(`${saved} prêmio(s) atualizado(s)!`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar quantidades');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('⚠️ ATENÇÃO: Isso vai APAGAR todos os baús distribuídos e zerar as quantidades de prêmios. Confirma?')) return;
    setResetting(true);
    try {
      const res = await mysteryBoxAPI.resetEvent();
      toast.success(`Evento resetado! ${res.data.results_deleted} baús apagados, ${res.data.weekly_prizes_deleted} prêmios semanais limpos.`);
      await load();
    } catch (e) {
      toast.error('Erro ao resetar evento');
    } finally {
      setResetting(false);
    }
  };

  const prizesByTier = (tier) => prizes.filter(p => p.tier === tier).sort((a, b) => b.points - a.points);

  const totalsByTier = {
    1: prizesByTier(1).reduce((s, p) => s + p.points * p.remaining_quantity, 0),
    2: prizesByTier(2).reduce((s, p) => s + p.points * p.remaining_quantity, 0),
    3: prizesByTier(3).reduce((s, p) => s + p.points * p.remaining_quantity, 0),
  };

  const hasChanges = Object.keys(edits).length > 0;

  if (loading) return <div className="text-center py-12 text-gray-500">Carregando prêmios...</div>;

  return (
    <div className="space-y-6" data-testid="mystery-box-prizes-config">
      <Card className="border-blue-200 bg-blue-50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            🎯 Configuração de Prêmios em PONTOS
          </CardTitle>
          <CardDescription>
            6 tipos de prêmio fixos × 3 tipos de baú. Defina a quantidade disponível de cada prêmio em cada baú.
            Os pontos coletados pelos clientes contam para o <strong>Ranking Semanal Mega Sortudos</strong> (que paga em moedas).
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button onClick={handleSave} disabled={!hasChanges || saving} className="bg-green-600 hover:bg-green-700" data-testid="save-prize-quantities">
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Salvando...' : `Salvar Alterações${hasChanges ? ` (${Object.keys(edits).length})` : ''}`}
          </Button>
          <Button variant="outline" onClick={load} disabled={saving}>
            Recarregar
          </Button>
        </div>
        <Button variant="destructive" onClick={handleReset} disabled={resetting} data-testid="reset-mystery-event">
          <RotateCcw className="mr-2 h-4 w-4" />
          {resetting ? 'Resetando...' : 'Resetar Evento'}
        </Button>
      </div>

      {[1, 2, 3].map(tier => {
        const info = TIER_INFO[tier];
        const Icon = info.icon;
        return (
          <Card key={tier} data-testid={`tier-card-${tier}`}>
            <CardHeader className={`bg-gradient-to-r ${info.color} text-white rounded-t-xl`}>
              <CardTitle className="flex items-center gap-3 text-white">
                <Icon className="h-6 w-6" />
                <span>{info.emoji} {info.name}</span>
                <Badge className="bg-white/20 border-white/30 text-white ml-auto">
                  {info.label}
                </Badge>
              </CardTitle>
              <CardDescription className="text-white/90">
                Total de pontos disponíveis neste baú: <strong>{totalsByTier[tier].toLocaleString()} pts</strong>
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {prizesByTier(tier).map(p => {
                  const used = p.total_quantity - p.remaining_quantity;
                  const editedVal = edits[p.id];
                  const currentVal = editedVal !== undefined ? editedVal : p.total_quantity;
                  const rar = RARITY_BADGE[p.rarity] || RARITY_BADGE.comum;
                  return (
                    <div key={p.id} className="border-2 rounded-lg p-3 bg-white hover:shadow-md transition-shadow" data-testid={`prize-${p.prize_type}-tier-${tier}`}>
                      <div className="flex items-start gap-3 mb-2">
                        {p.image_url && (
                          <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-gradient-to-br from-gray-900 to-black flex items-center justify-center overflow-hidden ring-2 ring-purple-200">
                            <img src={p.image_url} alt={p.name} className="w-full h-full object-contain" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 text-sm leading-tight">{p.name}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-lg font-bold text-purple-600">{p.points}<span className="text-xs ml-0.5">pts</span></span>
                            {p.rarity && <Badge variant="outline" className={`text-[10px] ${rar.cls}`}>{rar.label}</Badge>}
                          </div>
                        </div>
                      </div>
                      <Label className="text-xs text-gray-500">Quantidade disponível</Label>
                      <Input
                        type="number"
                        min={used}
                        value={currentVal}
                        onChange={(e) => handleChange(p.id, e.target.value)}
                        className={`h-9 mt-1 ${editedVal !== undefined ? 'border-blue-500 bg-blue-50' : ''}`}
                        data-testid={`qty-input-${p.prize_type}-tier-${tier}`}
                      />
                      <div className="flex items-center justify-between text-[11px] text-gray-500 mt-1.5">
                        <span>Restantes: <strong className="text-green-700">{p.remaining_quantity}</strong></span>
                        <span>Distribuídos: <strong className="text-orange-700">{used}</strong></span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}

      <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xs text-amber-700">Bronze (Baú do Destino)</p>
              <p className="text-2xl font-black text-amber-800">{totalsByTier[1].toLocaleString()}<span className="text-xs ml-1">pts</span></p>
            </div>
            <div>
              <p className="text-xs text-slate-600">Prata (Baú Místico)</p>
              <p className="text-2xl font-black text-slate-700">{totalsByTier[2].toLocaleString()}<span className="text-xs ml-1">pts</span></p>
            </div>
            <div>
              <p className="text-xs text-yellow-700">Ouro (Baú do Segredo)</p>
              <p className="text-2xl font-black text-yellow-700">{totalsByTier[3].toLocaleString()}<span className="text-xs ml-1">pts</span></p>
            </div>
          </div>
          <div className="text-center mt-4 pt-4 border-t border-purple-200">
            <p className="text-xs text-purple-700">Total Geral de Pontos Disponíveis</p>
            <p className="text-3xl font-black text-purple-900">{(totalsByTier[1] + totalsByTier[2] + totalsByTier[3]).toLocaleString()}<span className="text-sm ml-1">pts</span></p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MysteryBoxPrizesConfig;
